import React, { useEffect, useRef, useState } from 'react';
import { Article } from '../../Types/Article';
import Button from '../../Components/Button';
import { useNavigate } from '../../ProtectedRoute';
import { triggerUserArticleGeneration } from '../../API/ArticleApi';
import { ArticleAttributeAssignmentSlot } from '../../Types/ArticleAttributeAssignment';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { AnalyticsEvent, useAnalytics } from '../../Context/AnalyticsProvider';

interface ContinueArticleProps {
  article: Article | null;
}

const ContinueArticle: React.FC<ContinueArticleProps> = ({ article }) => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const [storyline, setStoryline] = useState<string>();
  const storylineRef = useRef<string>();
  storylineRef.current = storyline;
  const [isTriggeringGeneration, setIsTriggeringGeneration] = useState(false);
  const [validationError, setValidationError] = useState();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'Enter') {
        handleSubmit();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  async function handleSubmit(): Promise<void> {
    try {
      setIsTriggeringGeneration(true);
      setValidationError(undefined);

      const inheritedArticleAttributeAssignments =
        article.attributeAssignments?.filter((assignment) =>
          [
            ArticleAttributeAssignmentSlot.Genre,
            ArticleAttributeAssignmentSlot.Character,
            // We don't inherit Setting to let the LLM drive the story in different directions
          ].includes(assignment.slot),
        );

      const { requestId, newArticleId } = await triggerUserArticleGeneration({
        attributeIdsBySlot:
          inheritedArticleAttributeAssignments &&
          _.chain(inheritedArticleAttributeAssignments)
            .groupBy('slot')
            .mapValues((attributes) =>
              attributes.map((attr) => attr.attributeId),
            )
            .value(),
        storyline: storylineRef.current,
        baseArticleId: article.id,
      });

      navigate(`/create-my-own-story`, {
        requestId,
        newArticleId,
      });
    } catch (error) {
      if (
        error.response &&
        error.response.status === 422 &&
        error.response.data.errors &&
        error.response.data.errors?.['storyline']
      ) {
        setValidationError(error.response.data.errors?.['storyline']);

        // Handle validation errors
        trackEvent(AnalyticsEvent.STORY_BUILDER_CONTINUATION_REQUEST_FAILED, {
          error: 'validation',
          storyline: storyline,
          validationErrors: error.response.data.errors,
        });
      } else {
        toast.error('Something went wrong. Please try again.');
        trackEvent(AnalyticsEvent.STORY_BUILDER_CONTINUATION_REQUEST_FAILED, {
          error: 'unknown',
          message: error.message,
        });
      }

      console.error('Error triggering continue article', error);
    } finally {
      setIsTriggeringGeneration(false);
    }
  }

  return (
    <div className={`flex flex-col`}>
      <div
        className="
            flex flex-col md:p-3 xl:p-4 items-stretch gap-2
            max-sm:gap-1 max-sm:justify-between max-sm:p-4
            max-md:max-w-full
          "
      >
        <h3
          className="text-black text-xl font-medium
                max-sm:text-lg max-sm:font-bold
                max-md:max-w-full
            "
        >
          Continue Your Story
        </h3>
        <p className="text-gray-500 text-sm text-semibold">
          What happens next? You can tell us your ideas below, or just click
          'Continue' to see where the story goes!
        </p>
        <textarea
          className="border border-gray-300 rounded-md p-2 h-40"
          placeholder="(Optional) Add the next part of your story here…"
          defaultValue={storyline}
          autoFocus
          maxLength={1500}
          onChange={(e) => {
            setStoryline(e.target.value);
            setValidationError(undefined);
          }}
        />
        {validationError && (
          <div className="text-red-500 text-sm mt-1">{validationError}</div>
        )}
        <div className="flex justify-end">
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            isInProgress={isTriggeringGeneration}
            inProgressText="Continuing Story..."
            className="max-lg:w-full"
          >
            Continue Story
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ContinueArticle;
